import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, forkJoin, from, of, throwError } from 'rxjs';
import { map, mergeMap, takeUntil, tap, toArray } from 'rxjs/operators';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { SampleStatusEnum, getFilteredSampleStatuses } from 'src/app/enums/sampleStatus.enum';
import { getSelectableSampleTypes } from 'src/app/enums/sampleType.enum';
import { UnsampledTimeCorrectionType } from 'src/app/enums/unsampledTimeCorrectionType.enum';
import { FieldRenderType, FIELD_RENDER_TYPE_LABELS } from 'src/app/shared/form/formFieldType.enum';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { Lookup } from 'src/app/models/lookup.model';
import { LocalStorageConstants } from 'src/app/shared/constants/localStorage.constants';
import { readCachedSelection } from 'src/app/shared/utils/cached-selection.util';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CommonService } from 'src/app/services/common.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { SampleMediaService } from 'src/app/services/sample-media.service';
import { SampleSurveyService } from 'src/app/services/sample-survey.service';
import { SegService } from 'src/app/services/seg.service';
import { SampleService } from 'src/app/services/sample.service';
import { UserService } from 'src/app/services/user.service';
import { Sample } from 'src/app/models/sample.model';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import {
  BulkEditCategory,
  BulkEditCategoryView,
  BulkEditField,
  BulkEditOption,
  DialogStep,
  EditState,
  SampleUpdateResult
} from './bulk-edit-samples-dialog.models';
import {
  BULK_EDIT_EDITABLE_FIELD_IDS,
  BULK_EDIT_FIELD_CONSTRAINTS,
  BULK_EDIT_LIMITS,
  BULK_EDIT_NUMERIC_ENUM_FIELDS,
  BULK_EDIT_SURVEY_STATUS_FILTER,
  getBulkEditFieldCategories,
} from './bulk-edit-samples-dialog.constants';

@Component({
  standalone: false,
  selector: 'app-bulk-edit-samples-dialog',
  templateUrl: './bulk-edit-samples-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./bulk-edit-samples-dialog.component.scss']
})
export class BulkEditSamplesDialogComponent implements OnInit, OnDestroy {
  allFieldCategories: BulkEditCategory[] = [];
  allFields: BulkEditField[] = [];
  selectedFieldIds: string[] = [];
  selectedFieldSet = new Set<string>();
  selectedFields: BulkEditField[] = [];
  pickerCategories: BulkEditCategoryView[] = [];
  editFields: BulkEditField[] = [];
  editValues: EditState = {};
  editTimes: { [fieldId: string]: string | null } = {};
  pickerSearchTerm = '';
  currentStep: DialogStep = 'picker';

  readonly MAX_FIELDS = BULK_EDIT_LIMITS.MAX_FIELDS;
  readonly UPDATE_CONCURRENCY = BULK_EDIT_LIMITS.UPDATE_CONCURRENCY;
  readonly trackByIndex = trackByIndex;
  private readonly destroy$ = new Subject<void>();
  private resultTimeout?: ReturnType<typeof setTimeout>;

  processing = {
    total: 0,
    completed: 0,
    progressPercent: 0,
    fieldTags: [] as string[]
  };

  result = {
    successCount: 0,
    failureCount: 0,
    changedFields: [] as { name: string; value: string }[],
    failedSamples: [] as { id: string; error: string }[]
  };

  constructor(
    public dialogRef: MatDialogRef<BulkEditSamplesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sampleIds: string[]; samples?: Sample[] },
    private commonService: CommonService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private sampleService: SampleService,
    private organizationLevelService: OrganizationLevelService,
    private sampleSurveyService: SampleSurveyService,
    private segService: SegService,
    private userService: UserService,
    private sampleMediaService: SampleMediaService,
    private dateFormatService: DateFormatService
  ) {
    this.initializeFields();
  }

  ngOnInit(): void {
    this.loadDynamicOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resultTimeout) {
      clearTimeout(this.resultTimeout);
    }
  }

  private initializeFields(): void {
    this.allFieldCategories = getBulkEditFieldCategories();

    this.allFieldCategories.forEach(cat => {
      cat.fields.forEach(f => {
        this.allFields.push({ ...f, categoryIcon: cat.icon });
      });
    });

    // Only the selectable sample types — bulk-CONVERTING samples to a non-capturable type
    // (Other, Vibration) must not be possible here, matching the capture dialog's gate.
    this.setFieldOptions(nameOf<Sample>(x => x.sampleType), this.toOptions(getSelectableSampleTypes()));
    this.setFieldOptions(nameOf<Sample>(x => x.sampleCategory), this.toOptions(getEnumLookup(SampleCategoryEnum)));
    this.setFieldOptions(nameOf<Sample>(x => x.unsampledTimeCorrection), this.toOptions(getEnumLookup(UnsampledTimeCorrectionType)));
    this.setFieldOptions(nameOf<Sample>(x => x.sampleStatus), this.getStatusOptions());
    this.rebuildPickerCategories();
  }

  private loadDynamicOptions(): void {
    const selectedOrganizationId = this.getSelectedOrganizationId();
    const orgPayload = JSON.stringify({ organizationLevelId: selectedOrganizationId });

    forkJoin({
      sampleFields: this.sampleService.getFieldConfiguration(),
      orgLevels: this.organizationLevelService.getAll(),
      surveys: this.sampleSurveyService.getAll(JSON.stringify({ surveyStatus: BULK_EDIT_SURVEY_STATUS_FILTER })),
      segs: this.segService.getAll(orgPayload),
      users: this.userService.getAll(),
      media: this.sampleMediaService.getAll(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.applyFieldConfiguration(result.sampleFields);
        this.setFieldOptions(nameOf<Sample>(x => x.organizationLevelId), this.toOptions(result.orgLevels));
        this.setFieldOptions(nameOf<Sample>(x => x.sampleSurveyId), this.toOptions(result.surveys));
        this.setFieldOptions(nameOf<Sample>(x => x.segId), this.toOptions(result.segs));
        const userOptions = this.toOptions(result.users);
        this.setFieldOptions(nameOf<Sample>(x => x.sampledByUserId), userOptions);
        this.setFieldOptions(nameOf<Sample>(x => x.managingHygienistId), userOptions);
        this.setFieldOptions(nameOf<Sample>(x => x.sampleMediaId), this.toOptions(result.media));
        this.rebuildPickerCategories();
      },
      error: (error: unknown) => {
        this.toastService.showError(error);
      }
    });
  }

  private toOptions(items: Lookup[]): BulkEditOption[] {
    return (items || []).map(item => ({ id: item.id, name: item.name }));
  }

  private getSelectedOrganizationId(): string | undefined {
    const selectedOrg = this.commonService.selectedOrgEmitter.value as { id?: string } | null;
    if (selectedOrg?.id != null) {
      return selectedOrg.id;
    }

    return readCachedSelection<{ id?: string }>(LocalStorageConstants.selectedOrg)?.id;
  }

  private applyFieldConfiguration(fieldConfig: { [property: string]: ColumnDetails }): void {
    const fieldsById = new Map(this.allFields.map(field => [field.id, field]));
    Object.entries(fieldConfig || {}).forEach(([fieldId, config]) => {
      const field = fieldsById.get(fieldId);
      if (!field) {
        return;
      }
      if (config?.displayName) {
        field.name = config.displayName;
      }
      if (config?.unit) {
        field.unit = config.unit;
      }
    });
  }

  private setFieldOptions(fieldId: string, options: BulkEditOption[]): void {
    const field = this.allFields.find(f => f.id === fieldId);
    if (field) {
      field.options = options;
    }
  }

  private getStatusOptions(): BulkEditOption[] {
    const allStatuses = getEnumLookup(SampleStatusEnum);
    const samples = this.data.samples ?? [];
    const typeSet = new Set(samples.map(s => s.sampleType));
    const categorySet = new Set(samples.map(s => s.sampleCategory));
    const sampleType = typeSet.size === 1 ? Number([...typeSet][0]) : null;
    const sampleCategory = categorySet.size === 1 ? Number([...categorySet][0]) : null;
    const filtered = getFilteredSampleStatuses(allStatuses, sampleType, sampleCategory);
    return this.toOptions(filtered as Lookup[]);
  }

  get isPickerNextDisabled(): boolean {
    return this.selectedFieldIds.length === 0;
  }

  get isApplyDisabled(): boolean {
    return this.selectedFieldIds.some(fid => !this.isFieldFilled(fid));
  }

  get isAtFieldLimit(): boolean {
    return this.selectedFieldIds.length >= this.MAX_FIELDS;
  }

  onPickerSearchTermChange(searchTerm: string): void {
    this.pickerSearchTerm = searchTerm;
    this.rebuildPickerCategories();
  }

  private rebuildPickerCategories(): void {
    const term = this.pickerSearchTerm.toLowerCase().trim();
    this.pickerCategories = this.allFieldCategories
      .map(category => {
        const fields = category.fields
          .filter(field => {
            if (!term) {
              return true;
            }
            return field.name.toLowerCase().includes(term) || category.name.toLowerCase().includes(term);
          })
          .map(field => ({
            ...field,
            isSelected: this.selectedFieldSet.has(field.id),
            isDisabled: !this.selectedFieldSet.has(field.id) && this.isAtFieldLimit,
            typeLabel: FIELD_RENDER_TYPE_LABELS[field.type]
          }));

        return {
          name: category.name,
          icon: category.icon,
          fields
        };
      })
      .filter(category => category.fields.length > 0);
  }

  toggleField(fieldId: string): void {
    const idx = this.selectedFieldIds.indexOf(fieldId);
    if (idx >= 0) {
      this.selectedFieldIds.splice(idx, 1);
    } else {
      if (this.selectedFieldIds.length >= this.MAX_FIELDS) return;
      this.selectedFieldIds.push(fieldId);
    }
    this.updateSelectedFieldsState();
    this.rebuildPickerCategories();
  }

  getField(fieldId: string): BulkEditField | undefined {
    return this.allFields.find(f => f.id === fieldId);
  }

  removeField(fieldId: string): void {
    const idx = this.selectedFieldIds.indexOf(fieldId);
    if (idx >= 0) {
      this.selectedFieldIds.splice(idx, 1);
    }
    this.updateSelectedFieldsState();
    this.rebuildPickerCategories();
  }

  private updateSelectedFieldsState(): void {
    this.selectedFieldSet = new Set(this.selectedFieldIds);
    this.selectedFields = this.selectedFieldIds
      .map(fieldId => this.getField(fieldId))
      .filter((field): field is BulkEditField => !!field);
  }

  goToEditStep(): void {
    if (this.selectedFieldIds.length === 0) return;
    this.editValues = {};
    this.editTimes = {};
    this.editFields = [...this.selectedFields];
    this.currentStep = 'edit';
  }

  goBack(): void {
    if (this.currentStep === 'edit') {
      this.currentStep = 'picker';
    }
  }

  private hasValue(value: string | number | boolean | null | undefined): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private isFieldFilled(fieldId: string): boolean {
    if (!this.hasValue(this.editValues[fieldId])) return false;
    return this.getField(fieldId)?.type !== FieldRenderType.DATE_TIME || this.hasValue(this.editTimes[fieldId]);
  }

  private validateSelectedValues(): string | null {
    for (const fieldId of this.selectedFieldIds) {
      const field = this.getField(fieldId);
      if (!field) continue;
      const value = this.editValues[fieldId];
      if (!this.isFieldFilled(fieldId)) {
        return this.translateService.getDynamicTranslation('All fields are required');
      }

      if (field.type === FieldRenderType.SELECT && field.options?.length) {
        const valid = field.options.some(opt => opt.id === value);
        if (!valid) {
          return this.translateService.getDynamicTranslation('Invalid value selected for {{field}}', { field: this.translateService.getDynamicTranslation(field.name) });
        }
      }

      if (field.type === FieldRenderType.NUMBER) {
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
          return this.translateService.getDynamicTranslation('Invalid value selected for {{field}}', { field: this.translateService.getDynamicTranslation(field.name) });
        }
        const constraints = BULK_EDIT_FIELD_CONSTRAINTS[fieldId];
        if (constraints?.min != null && numeric < constraints.min) {
          return this.translateService.getDynamicTranslation('{{field}} must be at least {{min}}', { field: this.translateService.getDynamicTranslation(field.name), min: constraints.min });
        }
        if (constraints?.max != null && numeric > constraints.max) {
          return this.translateService.getDynamicTranslation('{{field}} must be at most {{max}}', { field: this.translateService.getDynamicTranslation(field.name), max: constraints.max });
        }
      }
    }
    return null;
  }

  startApply(): void {
    const validationMessage = this.validateSelectedValues();
    if (validationMessage) {
      this.toastService.displayErrorMessage(validationMessage);
      return;
    }

    this.currentStep = 'processing';
    this.processing.total = this.data.sampleIds.length;
    this.processing.completed = 0;
    this.processing.progressPercent = 0;
    this.processing.fieldTags = this.selectedFieldIds.map(id =>
      this.allFields.find(f => f.id === id)?.name || id
    );

    const samples = this.data.samples ?? [];
    if (!samples.length) {
      this.currentStep = 'edit';
      this.toastService.displayErrorMessage(this.translateService.getDynamicTranslation('No samples selected'));
      return;
    }

    from(samples).pipe(
      mergeMap(
        (sample: Sample) => this.updateSample(sample).pipe(
          map(() => ({
            isError: false,
            sampleId: sample.id,
            message: ''
          } as SampleUpdateResult)),
          catchError((err: { error?: { message?: string }; message?: string }) =>
            of<SampleUpdateResult>({
              isError: true,
              sampleId: sample.id,
              message: err?.error?.message || err?.message || ''
            })
          ),
          tap(() => {
            this.processing.completed += 1;
            this.processing.progressPercent = Math.round((this.processing.completed / this.processing.total) * 100);
          })
        ),
        this.UPDATE_CONCURRENCY
      ),
      toArray(),
      takeUntil(this.destroy$)
    ).subscribe((results: SampleUpdateResult[]) => {
      const failed = results.filter(
        (r: SampleUpdateResult) => r.isError
      );
      const succeeded = results.length - failed.length;

      this.result.successCount = succeeded;
      this.result.failureCount = failed.length;
      this.result.changedFields = this.selectedFieldIds.map(fid => {
        const field = this.allFields.find(f => f.id === fid);
        const val = this.editValues[fid];
        const displayValue = this.getDisplayValue(fid, val);
        const displayVal = displayValue.length > BULK_EDIT_LIMITS.MAX_DISPLAY_VALUE_LENGTH
          ? displayValue.substring(0, BULK_EDIT_LIMITS.MAX_DISPLAY_VALUE_LENGTH) + '...'
          : displayValue;
        return { name: field?.name || fid, value: displayVal };
      });
      this.result.failedSamples = failed.map(f => ({
        id: String(f.sampleId),
        error: f.message || this.translateService.getDynamicTranslation('Update failed')
      }));

      this.resultTimeout = setTimeout(() => { this.currentStep = 'result'; }, BULK_EDIT_LIMITS.RESULT_STEP_DELAY_MS);
    });
  }

  private updateSample(sample: Sample): Observable<unknown> {
    const scheduleEdits = this.getScheduleEdits(sample);
    if (!scheduleEdits) {
      return throwError(() => new Error(this.translateService.getDynamicTranslation('Set the Date and Time of Sampling before changing the End Time')));
    }
    return this.sampleService.update({ ...this.applyEditsToSample(sample), ...scheduleEdits });
  }

  private applyEditsToSample(sample: Sample): Sample {
    const updated = { ...sample } as Record<string, unknown>;
    for (const fieldId of this.selectedFieldIds) {
      if (BULK_EDIT_EDITABLE_FIELD_IDS.has(fieldId) && !this.isScheduleField(fieldId)) {
        updated[fieldId] = this.getPayloadValue(fieldId, this.editValues[fieldId]);
      }
    }
    return updated as unknown as Sample;
  }

  private isScheduleField(fieldId: string): boolean {
    return fieldId === nameOf<Sample>(x => x.startDate) || fieldId === nameOf<Sample>(x => x.endDate);
  }

  // The picked date and times are in the user's time zone. Like the sample dialog, End Time is a time of day on the
  // sampling date (the next day when it is earlier than the start), so moving the start keeps each sample's end time.
  // Returns null when End Time is set on a sample that has no Date and Time of Sampling to anchor it to.
  private getScheduleEdits(sample: Sample): Partial<Sample> | null {
    const startField = nameOf<Sample>(x => x.startDate);
    const endField = nameOf<Sample>(x => x.endDate);
    const startEdited = this.selectedFieldSet.has(startField);
    const endEdited = this.selectedFieldSet.has(endField);
    if (!startEdited && !endEdited) return {};

    const start = startEdited
      ? { date: String(this.editValues[startField]), time: String(this.editTimes[startField]) }
      : this.toLocalDateAndTime(sample.startDate);
    if (!start) return null;

    const startUtc = this.dateFormatService.localDateTimeToUTC(start.date, start.time);
    const edits: Partial<Sample> = startEdited ? { startDate: startUtc, dateCreated: startUtc } : {};
    if (sample.sampleCategory === SampleCategoryEnum.Blank) {
      edits.endDate = startUtc;
      return edits;
    }

    const endTime = endEdited ? String(this.editValues[endField]) : this.toLocalDateAndTime(sample.endDate)?.time;
    if (endTime) {
      const endDate = endTime < start.time ? this.dateFormatService.addDays(start.date, 1) : start.date;
      edits.endDate = this.dateFormatService.localDateTimeToUTC(endDate, endTime);
    }
    return edits;
  }

  private toLocalDateAndTime(utc: Sample['startDate'] | null | undefined): { date: string; time: string } | null {
    return typeof utc === 'string' && utc ? this.dateFormatService.utcToLocalDateAndTime(utc) : null;
  }

  private getPayloadValue(fieldId: string, value: string | number | boolean | null | undefined): string | number | boolean | null {
    if (value === null || value === undefined || value === '') return null;
    if (BULK_EDIT_NUMERIC_ENUM_FIELDS.has(fieldId)) {
      return Number(value);
    }
    return value;
  }

  private getDisplayValue(fieldId: string, value: string | number | boolean | null | undefined): string {
    const field = this.getField(fieldId);
    if (field?.type === FieldRenderType.DATE_TIME) {
      return `${value ?? ''} ${this.editTimes[fieldId] ?? ''}`.trim();
    }
    if (field?.type === FieldRenderType.SELECT && field.options?.length) {
      const option = field.options.find(opt => opt.id === value);
      if (option) return option.name;
    }
    return value == null ? '' : String(value);
  }

  editMoreFields(): void {
    this.selectedFieldIds = [];
    this.updateSelectedFieldsState();
    this.editFields = [];
    this.pickerSearchTerm = '';
    this.rebuildPickerCategories();
    this.currentStep = 'picker';
  }

  closeDialog(): void {
    if (this.currentStep !== 'processing') {
      this.dialogRef.close();
    }
  }

  onClose(): void {
    this.dialogRef.close(this.result.successCount > 0 ? true : false);
  }
}
